import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTributeNotifications } from '../../hooks/useTributeNotifications';

const {
  FiBell, FiBellOff, FiCheck, FiMail, FiSun, FiHeart,
  FiMessageCircle, FiChevronDown, FiSmartphone, FiX,
} = FiIcons;

const DeliveryBadge = ({ active }) => (
  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
    active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
  }`}>
    {active ? 'ON' : 'OFF'}
  </span>
);

const ToggleRow = ({ icon, label, description, checked, onChange, badge }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-brand-dark last:border-0">
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        checked ? 'bg-accent-gold/10 text-accent-gold' : 'bg-brand-light text-text-muted'
      }`}>
        <SafeIcon icon={icon} className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-accent-navy">{label}</p>
          {badge && <DeliveryBadge active={checked} />}
        </div>
        <p className="text-[11px] text-text-muted">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${
        checked ? 'bg-accent-gold' : 'bg-gray-200'
      }`}
      aria-checked={checked}
      role="switch"
    >
      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${
        checked ? 'left-6' : 'left-1'
      }`} />
    </button>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted pt-4 pb-1">
    {children}
  </p>
);

const TributeNotificationSettings = ({ memorialPageId, memorialName }) => {
  const { prefs, loading, savePrefs } = useTributeNotifications(memorialPageId);
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (loading || !prefs) return null;

  const handleToggle = async (key, val) => {
    const updated = { ...prefs, [key]: val };
    setSaving(true);
    await savePrefs(updated);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const anyOn = Object.values(prefs).some(Boolean);
  const activeCount = Object.values(prefs).filter(Boolean).length;

  return (
    <div className="bg-white rounded-[24px] border border-brand-dark overflow-hidden shadow-sm">
      {/* Header Toggle */}
      <button
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between p-5 hover:bg-brand-light/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
            anyOn ? 'bg-accent-gold/10' : 'bg-brand-light'
          }`}>
            <SafeIcon
              icon={anyOn ? FiBell : FiBellOff}
              className={`w-4 h-4 ${anyOn ? 'text-accent-gold' : 'text-text-muted'}`}
            />
          </div>
          <div className="text-left">
            <p className="text-sm font-black uppercase tracking-wider text-accent-navy">
              Announcement Settings
            </p>
            <p className="text-[11px] text-text-muted">
              {anyOn
                ? `${activeCount} of 4 notification${activeCount !== 1 ? 's' : ''} active`
                : 'All notifications are off'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {saved && (
            <span className="text-[10px] font-bold text-green-600 flex items-center gap-1">
              <SafeIcon icon={FiCheck} className="w-3 h-3" />
              Saved
            </span>
          )}
          {saving && (
            <div className="w-3 h-3 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
          )}
          <SafeIcon
            icon={FiChevronDown}
            className={`w-4 h-4 text-text-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-brand-dark">

              {/* ── Tribute Events Section ── */}
              <SectionLabel>Notify me when someone…</SectionLabel>

              <ToggleRow
                icon={FiSun}
                label="Lights a Candle"
                description="Get notified when a candle is lit"
                checked={prefs.notify_on_candle}
                onChange={v => handleToggle('notify_on_candle', v)}
              />
              <ToggleRow
                icon={FiHeart}
                label="Leaves Flowers"
                description="Get notified of flower tributes"
                checked={prefs.notify_on_flower}
                onChange={v => handleToggle('notify_on_flower', v)}
              />
              <ToggleRow
                icon={FiMessageCircle}
                label="Signs the Guestbook"
                description="Messages, doves & prayers"
                checked={prefs.notify_on_message}
                onChange={v => handleToggle('notify_on_message', v)}
              />

              {/* ── Delivery Methods Section ── */}
              <SectionLabel>Delivery methods</SectionLabel>

              <ToggleRow
                icon={FiSmartphone}
                label="In-App Notifications"
                description="Show alerts in your notification bell"
                checked={prefs.notify_inapp !== false}
                badge
                onChange={v => handleToggle('notify_inapp', v)}
              />
              <ToggleRow
                icon={FiMail}
                label="Email Notifications"
                description="Also send me an email summary"
                checked={prefs.notify_email}
                badge
                onChange={v => handleToggle('notify_email', v)}
              />

              {/* Turn All Off shortcut */}
              {anyOn && (
                <button
                  onClick={() => {
                    const allOff = {
                      notify_on_candle: false,
                      notify_on_flower: false,
                      notify_on_message: false,
                      notify_inapp: false,
                      notify_email: false,
                    };
                    setSaving(true);
                    savePrefs(allOff).then(() => {
                      setSaving(false);
                      setSaved(true);
                      setTimeout(() => setSaved(false), 2000);
                    });
                  }}
                  className="mt-4 w-full py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiBellOff} className="w-3.5 h-3.5" />
                  Turn off all notifications
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TributeNotificationSettings;